import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import EnhancedBidTable from '../components/bidtable/EnhancedBidTable';
import BidForm from '../components/bidform/bidform';
import { useAuth } from '../contexts/authcontext';
import { createBid } from '../services/api';
import styles from './dashboardpage.module.css';

function DashboardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const newBidType = queryParams.get('new');
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState(null);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    // Load the last selected tab from localStorage, default to 'project'
    return localStorage.getItem('zenbidup_bidtracker_tab') || 'project';
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [defaultSort, setDefaultSort] = useState({ key: 'JobNumber', order: 'asc' });

  useEffect(() => {
    if (newBidType) {
      setShowForm(true);
      setFormType(newBidType);
    } else {
      setShowForm(false);
      setFormType(null);
    }
    // Clear error when switching forms
    setError(null);
    setErrorDetails(null);
  }, [newBidType]);

  const handleCreateBid = async (bidData) => {
    if (!user) {
      setError('You must be logged in to create a bid');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setErrorDetails(null);
    
    try {
      console.log('Creating new bid with userId:', user.uid);
      console.log('Bid data:', bidData);
      
      // Ensure type is correctly set
      const updatedBidData = {
        ...bidData,
        // Make sure the type is correctly set for BidTable filtering
        type: bidData.type === 'project' ? 'incoming' : 'standalone',
      };
      
      await createBid(user.uid, updatedBidData);
      console.log('Bid created successfully!');
      
      // Set the active tab based on the bid type
      const newTab = bidData.type === 'project' ? 'project' : 'standalone';
      setActiveTab(newTab);
      localStorage.setItem('zenbidup_bidtracker_tab', newTab);
      
      // Navigate back to the bid list view after successful creation
      setShowForm(false);
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to create bid:', err);
      
      // Set the primary error message
      setError(`Failed to create bid: ${err.message || 'Unknown error'}`);
      
      // Collect additional error details for debugging
      let details = [];
      
      if (err.code) {
        console.error('Error code:', err.code);
        details.push(`Error code: ${err.code}`);
      }
      
      if (err.details) {
        console.error('Error details:', err.details);
        details.push(`Details: ${err.details}`);
      }
      
      // For Firestore permission errors, provide helpful guidance
      if (err.code === 'permission-denied' || err.message?.includes('permission')) {
        details.push('This may be due to Firestore security rules. Please check that you have updated the rules in the Firebase Console.');
        details.push('For debugging, you can set: allow write: if request.auth != null;');
      }
      
      if (details.length > 0) {
        setErrorDetails(details);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Navigate back to the bid list view when cancel is clicked
    setShowForm(false);
    setError(null);
    setErrorDetails(null);
    navigate('/dashboard');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('zenbidup_bidtracker_tab', tab);
    // Set default sort based on which tab is active
    if (tab === 'project') {
      setDefaultSort({ key: 'JobNumber', order: 'asc' });
    } else if (tab === 'standalone') {
      setDefaultSort({ key: 'ClientName', order: 'asc' });
    }
  };

  // Table component
  const TableComponent = EnhancedBidTable;

  return (
    <div className={styles.container} style={{ overflow: 'visible' }}>
      {error && (
        <div className={styles.error}>
          <div>{error}</div>
          {errorDetails && (
            <ul className={styles.errorDetails}>
              {errorDetails.map((detail, index) => (
                <li key={index}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      
      {showForm ? (
        <div className={styles.centeredFormContainer}>
          <h2 className={styles.pageTitle}>
            New {formType === 'project' ? 'Project' : 'Standalone'} Bid
          </h2>
          <div className={styles.formCardWrapper}>
            <BidForm 
              onSubmit={handleCreateBid} 
              onCancel={handleCancel} 
              bidType={formType} 
              isSubmitting={isSubmitting}
            />
          </div>
        </div>
      ) : (
        <>
          <div className={styles.tabsContainer}>
            <style>{`
              .zenbid-tooltip {
                position: relative;
                display: inline-flex;
                align-items: center;
                margin-left: 8px;
                cursor: help;
              }
              .zenbid-tooltip .zenbid-tooltiptext {
                visibility: hidden;
                width: 240px;
                background-color: #2d3748;
                color: #e0e0e0;
                text-align: left;
                border-radius: 4px;
                padding: 8px 12px;
                position: absolute;
                z-index: 10;
                top: 120%;
                left: 50%;
                transform: translateX(-50%);
                opacity: 0;
                transition: opacity 0.3s;
                font-size: 13px;
                font-family: var(--font-primary, 'Poppins', sans-serif);
                box-shadow: 0 2px 12px #0006;
                pointer-events: none;
              }
              .zenbid-tooltip:hover .zenbid-tooltiptext {
                visibility: visible;
                opacity: 1;
                pointer-events: auto;
              }
              .zenbid-questionMark {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 22px;
                height: 22px;
                font-size: 14px;
                color: #e0e0e0;
                background-color: #4a5568;
                border-radius: 50%;
                border: 1.5px solid #3498db;
                margin-left: 7px;
                font-weight: 700;
                box-shadow: 0 1px 4px #0004;
                transition: background 0.2s, border 0.2s;
              }
              .zenbid-tooltip:hover .zenbid-questionMark {
                background-color: #2980b9;
                border-color: #5dade2;
              }
              .zenbid-tabButton {
                padding: 10px 20px;
                background-color: #23233a;
                color: #e0e0e0;
                border: 1.5px solid #4a5568;
                border-radius: 7px;
                margin-right: 14px;
                font-size: 14px;
                font-family: var(--font-primary, 'Poppins', sans-serif);
                cursor: pointer;
                transition: background 0.3s, transform 0.1s, box-shadow 0.2s;
                box-shadow: 0 2px 8px rgba(30,30,47,0.18);
                display: inline-flex;
                align-items: center;
                font-weight: 600;
                letter-spacing: 0.2px;
                outline: none;
              }
              .zenbid-tabButton:hover {
                background-color: #2d3748;
                transform: translateY(-2px) scale(1.03);
                box-shadow: 0 4px 16px #3498db33;
              }
              .zenbid-tabButton.active {
                background-color: #3498db;
                color: #fff;
                border-color: #3498db;
                box-shadow: 0 4px 16px #3498db55;
              }
              .zenbid-tabButton.active:hover {
                background-color: #2980b9;
                color: #fff;
                border-color: #2980b9;
              }
            `}</style>
            <button 
              onClick={() => handleTabChange('project')} 
              className={`zenbid-tabButton${activeTab === 'project' ? ' active' : ''}`}
            >
              Project Bids
              <span className="zenbid-tooltip">
                <span className="zenbid-questionMark">?</span>
                <span className="zenbid-tooltiptext">
                  Project Bids: Manage bids for large-scale projects with multiple sub-bids and client details.
                </span>
              </span>
            </button>
            <button 
              onClick={() => handleTabChange('standalone')} 
              className={`zenbid-tabButton${activeTab === 'standalone' ? ' active' : ''}`}
            >
              Standalone Bids
              <span className="zenbid-tooltip">
                <span className="zenbid-questionMark">?</span>
                <span className="zenbid-tooltiptext">
                  Standalone Bids: Manage individual bids not tied to a larger project, ideal for quick jobs.
                </span>
              </span>
            </button>
          </div>
          
          {activeTab === 'project' && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Project Bids</h2>
              <TableComponent bidView="incoming" defaultSort={defaultSort} />
            </section>
          )}

          {activeTab === 'standalone' && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Standalone Bids</h2>
              <TableComponent bidView="standalone" defaultSort={defaultSort} />
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default DashboardPage; 